import React from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  App,
  Typography,
  Space,
  Progress,
  Alert,
  theme,
} from 'antd';
import { useLanguage } from 'src/providers/LanguageProvider';
import api from 'src/services/api';

const { Text } = Typography;

type FormValues = {
  NewPassword: string;
  ConfirmPassword: string;
};

function calcStrength(pw: string) {
  let score = 0;
  if (!pw) return { score: 0, percent: 0, level: 'weak' as const };
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;

  const percent = Math.min(100, Math.round((score / 6) * 100));
  let level: 'weak' | 'medium' | 'strong' = 'weak';
  if (percent >= 70 && pw.length >= 10) level = 'medium';
  if (percent >= 85 && pw.length >= 12) level = 'strong';
  return { score, percent, level };
}

const ChangePasswordForm: React.FC = () => {
  const { t } = useLanguage();
  const { message: messageApi } = App.useApp();
  const { token } = theme.useToken();

  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const newPw = Form.useWatch('NewPassword', form);
  const { percent, level } = calcStrength(newPw || '');

  const levelColor =
    level === 'strong' ? token.colorSuccess :
    level === 'medium' ? token.colorWarning :
    token.colorError;

  const onKeyEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    try {
      setCapsLock(e.getModifierState?.('CapsLock') ?? false);
    } catch {/* noop */}
  };

  const handleFinish = async (values: FormValues) => {
    setApiError(null);
    setSubmitting(true);
    try {
      await api.post('/accounts/password/change', {
        newPassword: values.NewPassword,
      });
      messageApi.success(t('PASSWORD_UPDATED'));
      form.resetFields();
    } catch (err: any) {

      const isDetail = err?.response?.data?.details?.error_code;
      if (isDetail ) {
        
        
        messageApi.error(t(isDetail))

        setSubmitting(false);
        return;
      }

      const msg = err?.response?.data?.message || err?.message || t('ERROR_GENERIC');
      setApiError(msg);
      messageApi.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const rules = {
    requiredNew: [
      { required: true, message: t('ERROR_NEW_PASSWORD_REQUIRED') },
      { min: 8, message: t('ERROR_MIN8') },
      {
        validator: async (_: any, value: string) => {
          if (!value) return;
          const fails: string[] = [];
          if (!/[a-z]/.test(value)) fails.push(t('RULE_LOWER'));
          if (!/[A-Z]/.test(value)) fails.push(t('RULE_UPPER'));
          if (!/\d/.test(value)) fails.push(t('RULE_DIGIT'));
          if (!/[^A-Za-z0-9]/.test(value)) fails.push(t('RULE_SPECIAL'));
          if (fails.length) return Promise.reject(new Error(fails.join(', ')));
          return Promise.resolve();
        },
      },
    ],
    confirm: [
      { required: true, message: t('ERROR_CONFIRM_PASSWORD_REQUIRED') },
      ({ getFieldValue }: any) => ({
        validator(_: any, value: string) {
          if (!value || getFieldValue('NewPassword') === value) return Promise.resolve();
          return Promise.reject(new Error(t('ERROR_PASSWORDS_NOT_MATCH')));
        },
      }),
    ],
  };

  return (
    <Card className="card" styles={{ body: { paddingTop: 16 } }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
      >
        {apiError && (
          <Alert
            type="error"
            showIcon
            message={t('ERROR_GENERIC')}
            description={apiError}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t('LABEL_NEW_PASSWORD')}
              name="NewPassword"
              rules={rules.requiredNew}
              hasFeedback
              validateTrigger={['onChange','onBlur']}
              extra={
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary">{t('PASSWORD_STRENGTH')}:</Text>{' '}
                    <Text strong style={{ color: levelColor }}>
                      {level === 'strong'
                        ? t('STRONG')
                        : level === 'medium'
                        ? t('MEDIUM')
                        : t('WEAK')}
                    </Text>
                  </div>
                  <Progress
                    percent={percent}
                    showInfo={false}
                    strokeColor={levelColor}
                    status={percent === 100 ? 'normal' : 'active'}
                  />
                  <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                    <li>{t('RULE_MIN8')}</li>
                    <li>{t('RULE_COMPLEXITY')}</li>
                  </ul>
                </Space>
              }
            >
              <Input.Password
                placeholder={t('PLACEHOLDER_NEW_PASSWORD')}
                autoComplete="new-password"
                onKeyUp={onKeyEvent}
              />
            </Form.Item>

            <Form.Item
              label={t('LABEL_CONFIRM_PASSWORD')}
              name="ConfirmPassword"
              dependencies={['NewPassword']}
              rules={rules.confirm}
              hasFeedback
              validateTrigger={['onChange','onBlur']}
            >
              <Input.Password
                placeholder={t('PLACEHOLDER_CONFIRM_PASSWORD')}
                autoComplete="new-password"
                onKeyUp={onKeyEvent}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Card
              size="small"
              bordered
              style={{ background: token.colorFillTertiary }}
              title={t('TIPS_TITLE')}
            >
              <Space direction="vertical" size={8}>
                <Text type="secondary">• {t('TIP_UNIQUE')}</Text>
                <Text type="secondary">• {t('TIP_LENGTH')}</Text>
                <Text type="secondary">• {t('TIP_MANAGER')}</Text>
                {capsLock && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('CAPS_LOCK_ON')}
                    description={t('CAPS_LOCK_DESC')}
                  />
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        <Form.Item shouldUpdate>
          {() => {
            const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
            const { NewPassword, ConfirmPassword } = form.getFieldsValue([
              'NewPassword','ConfirmPassword'
            ]) as FormValues;
            const allFilled = !!NewPassword && !!ConfirmPassword;

            return (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <Button onClick={() => form.resetFields()}>
                  {t('BTN_RESET')}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={!allFilled || hasErrors}
                >
                  {t('BTN_UPDATE_PASSWORD')}
                </Button>
              </div>
            );
          }}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ChangePasswordForm;
